import { calcularHorasSLA } from './slaUtils';
import type { Card, Flow } from '../types';

/**
 * Prazo do processo: um relógio só para o card inteiro, não etapa a etapa.
 *
 * O ciclo começa quando o card é criado e vai consumindo as horas enquanto ele
 * anda pelas etapas, sem ganhar fôlego novo a cada uma. Ao entrar numa etapa
 * de pausa (Reprovados, Concluídos...) o ciclo fecha e fica congelado com o
 * tempo que levou. Quando o card sai da pausa (ex.: o solicitante reenvia e ele
 * volta para Análise), nasce um ciclo novo com o prazo cheio.
 *
 * Tudo é derivado do etapaHistory, então vale também para cards antigos.
 */

export interface CicloPrazo {
  numero: number;
  inicio: Date;
  fim: Date | null;          // null = ciclo em andamento
  horasGastas: number;
}

export interface ResultadoPrazo {
  horasPrazo: number;
  ciclos: CicloPrazo[];
  atual: CicloPrazo | null;  // null = card parado numa etapa de pausa
  horasRestantes: number | null;
}

/** Etapas que congelam o prazo: as marcadas na config ou, sem config, as de reprovação com reenvio. */
export function etapasDePausa(flow: Pick<Flow, 'etapas' | 'prazoProcesso'>): Set<string> {
  const marcadas = flow.prazoProcesso?.etapasPausaIds;
  if (marcadas && marcadas.length) return new Set(marcadas.map(String));
  return new Set(
    (flow.etapas || []).filter(e => e.solicitarReenvioAnexos).map(e => String(e.id)),
  );
}

function data(v: any): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
}

export function calcularPrazoProcesso(
  card: Pick<Card, 'etapaHistory' | 'createdAt' | 'etapaId'>,
  flow: Pick<Flow, 'etapas' | 'prazoProcesso'>,
  agora: Date = new Date(),
): ResultadoPrazo | null {
  const cfg = flow.prazoProcesso;
  if (!cfg?.habilitado || !(cfg.horas > 0)) return null;

  const pausa = etapasDePausa(flow);
  const ignorarFds = !!cfg.ignorarFimDeSemana;
  const historico = [...(card.etapaHistory || [])]
    .map(h => ({ etapaId: String(h.etapaId), quando: data(h.movedAt) }))
    .filter((h): h is { etapaId: string; quando: Date } => !!h.quando)
    .sort((a, b) => a.quando.getTime() - b.quando.getTime());

  const ciclos: CicloPrazo[] = [];
  let aberto: CicloPrazo | null = null;
  const abrir = (inicio: Date) => {
    aberto = { numero: ciclos.length + 1, inicio, fim: null, horasGastas: 0 };
    ciclos.push(aberto);
  };

  // Início: criação do card (ou a primeira entrada no histórico, se for antes)
  const criado = data(card.createdAt);
  const primeira = historico[0];
  const inicio = criado && (!primeira || criado <= primeira.quando) ? criado : primeira?.quando;
  const etapaInicial = primeira?.etapaId ?? String(card.etapaId);
  if (inicio && !pausa.has(etapaInicial)) abrir(inicio);

  for (const h of historico) {
    if (pausa.has(h.etapaId)) {
      if (aberto) {
        (aberto as CicloPrazo).fim = h.quando;
        aberto = null;
      }
    } else if (!aberto) {
      abrir(h.quando);
    }
  }

  for (const c of ciclos) {
    c.horasGastas = calcularHorasSLA(c.inicio, c.fim ?? agora, ignorarFds);
  }

  const atual = ciclos.find(c => c.fim === null) || null;
  return {
    horasPrazo: cfg.horas,
    ciclos,
    atual,
    horasRestantes: atual ? cfg.horas - atual.horasGastas : null,
  };
}

/** 5.5 -> "5h30", 0.4 -> "24min", 30 -> "1d 6h" */
export function formatarDuracao(horas: number): string {
  const totalMin = Math.round(Math.abs(horas) * 60);
  if (totalMin < 60) return `${totalMin}min`;
  const totalH = Math.floor(totalMin / 60);
  if (totalH >= 24) {
    const d = Math.floor(totalH / 24);
    const r = totalH % 24;
    return r ? `${d}d ${r}h` : `${d}d`;
  }
  const min = totalMin % 60;
  return min ? `${totalH}h${String(min).padStart(2, '0')}` : `${totalH}h`;
}
